import { campaignEditorFormName, CampaignEditorPageFormsWrapper } from "./campaign_editor_helpers";
import React, { useContext, useEffect, useState } from "react";
import { change, Field } from "redux-form";
import { connect } from "react-redux";
import FormElement from "../../forms/form_element";
import { RadioSelectFrameField } from "../../lib/radio-select-frame/radio-select-frame";
import { If } from "react-extras";
import Divider from "../../lib/divider";
import Checkbox from "../../forms/checkbox";
import { RadioInput } from "../../lib/radio-input/radio-input";
import {
  CampaignStyleHostRead,
  DistributionTypeContinuous,
  DistributionTypeDiscrete,
} from "../../../constants/campaigns";
import { UserContext } from "../../app";
import { shouldShowDiscrete } from "../../../lib/feature_flag";
import WarningPanelv2 from "../../lib/warning_panel/warning_panel_v2";
import usePrevious from "../../../hooks/use_previous";

const DistributionTypeOptions = props => {
  let distributionTypeOptions = [
    {
      tag: "Recommended",
      optionValue: DistributionTypeContinuous,
      title: "All Episodes",
      description:
        "Your ad will play on a podcast’s entire catalog during the time you specify. Anyone who listens to any episodes on this podcast during that time may hear the ad.",
    },
    {
      optionValue: DistributionTypeDiscrete,
      title: "Select Episodes",
      description:
        "Your ad will only play on the episodes released during the time you specify, and will persist for 90 days. Only people who listen to that specific episode in its first 90 days will hear the ad.",
    },
  ];

  return (
    <Field
      name={"distributionType"}
      label={"Where would you like this audio distributed?"}
      component={FormElement}
      InputComponent={RadioSelectFrameField}
      options={distributionTypeOptions}
      defaultValue={DistributionTypeContinuous}
    />
  );
};

const DistributionTypeSingle = props => {
  return (
    <Field
      name={"distributionType"}
      component={FormElement}
      InputComponent={() => (
        <WarningPanelv2>
          <div className="margin-all--large">
            <h3 className="bold no-margin m-bxxxs no-padding">All Episodes</h3>
            <div>
              Your ad will play on a podcast’s entire catalog during the time you specify. Anyone
              who listens to any episodes on this podcast during that time may hear the ad.
            </div>
          </div>
        </WarningPanelv2>
      )}
      defaultValue={DistributionTypeContinuous}
    />
  );
};

const getDefaultSelectEpisodesPos = formValues => {
  if (!formValues) return "Mid-Roll";
  const { targetingOptions = {} } = formValues;
  if (targetingOptions.midRoll) {
    return "Mid-Roll";
  } else if (targetingOptions.preRoll) {
    return "Pre-Roll";
  }
  return "Post-Roll";
};

export let DistributionReach = ({
  handleSubmit,
  validationErrors,
  formValues,
  campaign,
  changeField,
}) => {
  const distributionType = formValues.distributionType;
  // prevDistributionType lets us set default states for the positions whenever we switch
  // between 'discrete' and 'continuous'. See useEffect below.
  const prevDistributionType = usePrevious(distributionType);

  // position is only used for 'Select Episodes' (discrete) flow
  const [position, setPosition] = useState(getDefaultSelectEpisodesPos(formValues));
  const user = useContext(UserContext);

  // Update targetingOptions when we switch between radio-buttons and checkboxes
  // Default to 'mid-roll' for Select Episodes
  // Default to all positions for All Episodes
  useEffect(() => {
    if (prevDistributionType === undefined) return;

    if (
      prevDistributionType !== DistributionTypeDiscrete &&
      distributionType === DistributionTypeDiscrete
    ) {
      changeField("targetingOptions.preRoll", false);
      changeField("targetingOptions.midRoll", true);
      changeField("targetingOptions.postRoll", false);
    } else if (
      prevDistributionType !== DistributionTypeContinuous &&
      distributionType === DistributionTypeContinuous
    ) {
      changeField("targetingOptions.preRoll", true);
      changeField("targetingOptions.midRoll", true);
      changeField("targetingOptions.postRoll", true);
    }
  }, [campaign, changeField, distributionType]);

  useEffect(() => {
    if (distributionType !== DistributionTypeDiscrete) return;
    changeField("targetingOptions.preRoll", position === "Pre-Roll");
    changeField("targetingOptions.midRoll", position === "Mid-Roll");
    changeField("targetingOptions.postRoll", position === "Post-Roll");
  }, [position, changeField]);

  const showSelectEpisodesOption =
    (user?.invoicingPaymentEnabled ?? false) &&
    shouldShowDiscrete() &&
    campaign.style === CampaignStyleHostRead;

  if (!campaign) return false;

  return (
    <form onSubmit={handleSubmit}>
      <If condition={showSelectEpisodesOption}>
        <DistributionTypeOptions />
      </If>
      <If condition={!showSelectEpisodesOption}>
        <DistributionTypeSingle />
      </If>
      <Divider />
      <label className="redcircle-form-label control-label">
        where in an episode would you like this audio placed?*
      </label>
      <If condition={distributionType === DistributionTypeContinuous}>
        <Field
          component={FormElement}
          InputComponent={Checkbox}
          name="targetingOptions.preRoll"
          checkboxLabel="Pre-Roll"
          type="checkbox"
          validationErrors={validationErrors}
        />
        <Field
          component={FormElement}
          InputComponent={Checkbox}
          name="targetingOptions.midRoll"
          checkboxLabel="Mid-Roll"
          type="checkbox"
          validationErrors={validationErrors}
        />
        <Field
          component={FormElement}
          InputComponent={Checkbox}
          name="targetingOptions.postRoll"
          checkboxLabel="Post-Roll"
          type="checkbox"
          validationErrors={validationErrors}
        />
      </If>
      <If condition={distributionType === DistributionTypeDiscrete}>
        {["Pre-Roll", "Mid-Roll", "Post-Roll"].map(pos => {
          return (
            <div className="margin-top--small" key={pos}>
              <RadioInput
                checked={position === pos}
                labelContent={() => <div className={"radio-input-label"}>{pos}</div>}
                onClick={() => {
                  setPosition(pos);
                }}
              />
            </div>
          );
        })}
      </If>
    </form>
  );
};

const mapDispatchToProps = {
  changeField: (field, value) => change(campaignEditorFormName, field, value),
};

DistributionReach = CampaignEditorPageFormsWrapper(
  connect(() => ({}), mapDispatchToProps)(DistributionReach)
);
